import { platformApi } from "@/api/http";

export interface Customer {
  id: number;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  notes?: string;
  /** Birthday day/month only (no year) — captured during the optional first-visit intake. */
  birthMonth?: number;
  birthDayOfMonth?: number;
  allergies?: string;
  baselineNotes?: string;
}

export interface CustomerPage {
  content: Customer[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface CreateCustomerRequest {
  firstName: string;
  lastName?: string;
  email?: string;
  phone?: string;
  genderId?: number;
  notes?: string;
  birthMonth?: number;
  birthDayOfMonth?: number;
  allergies?: string;
  baselineNotes?: string;
}

export interface CustomerAppointment {
  id: number;
  appointmentDate: string;
  startTime: string;
  serviceName: string;
  userName: string;
  stateCode: string;
  stateName: string;
  rating?: number;
}

const basePath = (businessId: string) =>
  `/api/v1/businesses/${businessId}/customers`;

function normalizeCustomer(raw: unknown): Customer {
  const r = raw as Record<string, unknown>;
  return {
    id: Number(r.id ?? 0),
    firstName: String(r.firstName ?? r.first_name ?? ""),
    lastName: String(r.lastName ?? r.last_name ?? ""),
    email: (r.email as string) ?? undefined,
    phone: (r.phone as string) ?? undefined,
    notes: (r.notes as string) ?? undefined,
    birthMonth: r.birthMonth != null ? Number(r.birthMonth) : undefined,
    birthDayOfMonth: r.birthDayOfMonth != null ? Number(r.birthDayOfMonth) : undefined,
    allergies: (r.allergies as string) ?? undefined,
    baselineNotes: (r.baselineNotes as string) ?? undefined,
  };
}

export const customersApi = {
  getById: async (businessId: string, id: number): Promise<Customer> => {
    const { data } = await platformApi.get(`${basePath(businessId)}/${id}`);
    return normalizeCustomer(data);
  },

  search: async (businessId: string, term: string): Promise<Customer[]> => {
    const { data } = await platformApi.post(`${basePath(businessId)}/search`, { term });
    const arr = Array.isArray(data) ? data : (data as Record<string, unknown>)?.data;
    return Array.isArray(arr) ? arr.map(normalizeCustomer) : [];
  },

  /**
   * Paged search — the picker only ever shows a screenful, and the unpaged endpoint
   * returns the business's entire customer book.
   */
  searchPaged: async (
    businessId: string,
    term: string,
    page: number,
    size: number,
  ): Promise<CustomerPage> => {
    const { data } = await platformApi.post(
      `${basePath(businessId)}/search/page`,
      { term },
      { params: { page, size } },
    );
    const body = (data ?? {}) as Record<string, unknown>;
    const content = Array.isArray(body.content) ? body.content : [];
    return {
      content: content.map(normalizeCustomer),
      page: Number(body.page ?? 0),
      size: Number(body.size ?? size),
      totalElements: Number(body.totalElements ?? content.length),
      totalPages: Number(body.totalPages ?? 1),
    };
  },

  create: async (businessId: string, request: CreateCustomerRequest): Promise<Customer> => {
    const { data } = await platformApi.post(basePath(businessId), request);
    return normalizeCustomer(data);
  },

  getAppointments: async (businessId: string, customerId: number): Promise<CustomerAppointment[]> => {
    const { data } = await platformApi.get(`${basePath(businessId)}/${customerId}/appointments`);
    return Array.isArray(data) ? data : [];
  },
};
